import { useEffect, useState } from 'react';

import { Button, Form, Card, InputGroup, Row, Col, Alert, Badge } from 'react-bootstrap';
import { FaEye, FaEyeSlash, FaUserEdit, FaShieldAlt, FaInfoCircle } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import FormContainer from '../../components/form/FormContainer';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import { useGetUserDetailsQuery, useUpdateUserMutation } from '../../slices/userApiSlice';
import { useGetCompaniesQuery } from '../../slices/companyApiSlice';
import { setCredentials } from '../../slices/authSlice';
import DocIdGroup from '../../components/user/DocIdGroup';
import { fillFromContifico } from '../../utils/userUtils';
import { sanitizeInput } from '../../utils/formUtils';

const UserEditScreen = ({ userId: userIdProp, onClose, onSaved, embedded = false }) => {
    //
    const { id: userIdParam } = useParams();
    const userId = userIdProp || userIdParam;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { userInfo: loggedInUser } = useSelector((state) => state.auth);

    const [docId, setDocId] = useState('');
    const [validDocId, setValidDocId] = useState(false);
    const [type, setType] = useState('');
    const [taxDocId, setTaxDocId] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isForeigner, setIsForeigner] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isClient, setIsClient] = useState(true);
    const [priceAssigned, setPriceAssigned] = useState('pvp1');
    const [originalIsAdmin, setOriginalIsAdmin] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyRuc, setSelectedCompanyRuc] = useState('');
    const [selectedCompanyRole, setSelectedCompanyRole] = useState('OPERATOR');

    const {
        data: user,
        isLoading: isLoadingUser,
        refetch: refetchUser,
    } = useGetUserDetailsQuery(userId);

    const [update, { isLoading }] = useUpdateUserMutation();
    const { data: availableCompanies = [], isLoading: loadingCompanies } = useGetCompaniesQuery(undefined, {
        skip: !loggedInUser?.isSuperAdmin,
    });

    const fillForm = (cPerson) => {
        fillFromContifico(
            cPerson,
            setName,
            setEmail,
            setType,
            setTaxDocId,
            setIsForeigner,
            setIsClient,
            setPriceAssigned
        );
    };

    useEffect(() => {
        if (user) {
            setType(user.type);
            setDocId(user.taxDocId ? user.taxDocId : user.docId);
            setTaxDocId(user.taxDocId ? user.taxDocId : '');
            setIsAdmin(user.isAdmin);
            setIsSuperAdmin(user.isSuperAdmin || false);
            setOriginalIsAdmin(user.isAdmin);
            setIsForeigner(user.isForeigner);
            setName(user.name);
            setEmail(user.email);
            setIsClient(user.isClient);
            setPriceAssigned(user.priceAssigned);
            setCompanies(Array.isArray(user.companies) ? user.companies : []);
        }
    }, [user]);

    const canEditCompanyPermissions = loggedInUser?.isSuperAdmin;
    const shouldManageCompanyPermissions = isAdmin && !isSuperAdmin;

    const handleAddCompanyPermission = () => {
        if (!selectedCompanyRuc) {
            toast.error('Selecciona una empresa');
            return;
        }

        if (companies.some((company) => company.ruc === selectedCompanyRuc)) {
            toast.error('Esta empresa ya fue agregada');
            return;
        }

        setCompanies((prev) => [...prev, { ruc: selectedCompanyRuc, role: selectedCompanyRole }]);
        setSelectedCompanyRuc('');
    };

    const handleCompanyRoleChange = (ruc, role) => {
        setCompanies((prev) => prev.map((company) => (company.ruc === ruc ? { ...company, role } : company)));
    };

    const handleRemoveCompanyPermission = (ruc) => {
        setCompanies((prev) => prev.filter((company) => company.ruc !== ruc));
    };

    const getCompanyName = (ruc) => {
        const company = availableCompanies?.find((item) => item.ruc === ruc);
        return company?.tradeName || ruc;
    };

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!validDocId) {
            toast.error('Identificación Inválida');
            return;
        }

        if (password && password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        // Validate role changes based on current user permissions
        if (loggedInUser.isAdmin && !loggedInUser.isSuperAdmin) {
            // Admin trying to revoke admin access
            if (originalIsAdmin && !isAdmin) {
                toast.error('Solo un superadmin puede revocar acceso de administrador');
                return;
            }
            // Admin trying to change superadmin status
            if (isSuperAdmin !== user?.isSuperAdmin) {
                toast.error('No tienes autorización para administrar roles de superadmin');
                return;
            }
            if (!originalIsAdmin && isAdmin) {
                toast.error('Solo un superadmin puede asignar permisos por empresa a un administrador');
                return;
            }
        }

        if (shouldManageCompanyPermissions && !canEditCompanyPermissions) {
            toast.error('Solo superadmins pueden administrar permisos por empresa');
            return;
        }

        if (shouldManageCompanyPermissions && companies.length === 0) {
            toast.error('Un administrador debe tener al menos una empresa asignada');
            return;
        }

        try {
            await updateUserData();
        } catch (err) {
            const message = err?.data?.message || err.error;

            // Map backend errors to user-friendly messages
            if (message?.includes('Not authorized to assign superadmin')) {
                toast.error('Solo superadmins pueden administrar roles de superadmin');
            } else if (message?.includes('Not authorized to revoke admin')) {
                toast.error('Solo superadmins pueden revocar acceso de administrador');
            } else if (message?.includes('Not authorized to assign admin')) {
                toast.error('No tienes permiso para asignar roles de administrador');
            } else {
                toast.error(message || 'Error al actualizar usuario');
            }
        }
    };

    const updateUserData = async () => {
        try {
            const payload = {
                userId,
                type: type === 'NF' ? 'N' : type,
                docId: taxDocId ? (type === 'J' ? '' : docId.slice(0, -3)) : docId,
                taxDocId,
                name,
                email,
                password,
                isClient,
                isForeigner,
                priceAssigned,
            };

            // Only include role fields if user has permission
            if (loggedInUser.isSuperAdmin) {
                // SuperAdmin can change both isAdmin and isSuperAdmin
                payload.isAdmin = isAdmin;
                payload.isSuperAdmin = isSuperAdmin;
                payload.companies = shouldManageCompanyPermissions ? companies : [];
            } else if (loggedInUser.isAdmin) {
                // Admin can only grant isAdmin (not revoke)
                if (isAdmin === true && !originalIsAdmin) {
                    payload.isAdmin = true;
                }
                // Don't include isSuperAdmin at all for admins
            }

            const res = await update(payload).unwrap();
            toast.success('Usuario ' + res.name + ' Actualizado con Éxito');
            
            // If the updated user is the same as the logged-in user, refresh their info in state
            if (loggedInUser && loggedInUser._id === userId) {
                dispatch(setCredentials(res));
            }

            refetchUser();

            if (embedded) {
                if (onSaved) {
                    onSaved(res);
                }
                if (onClose) {
                    onClose();
                }
                return;
            }

            navigate('/admin/userlist');
        } catch (err) {
            throw err;
        }
    };

    const formContent = (
        <>
            <Meta title="Editar Usuario" />

            {!embedded && (
                <div className="mb-4">
                    <h1 className="d-flex align-items-center gap-2">
                        <FaUserEdit className="text-primary" />
                        Editar Usuario
                    </h1>
                </div>
            )}

            {isLoadingUser && <Loader />}

            <Card className={`shadow-sm border-0 ${embedded ? '' : 'mb-4'}`}>
                {!embedded && (
                    <Card.Header className="bg-primary text-white py-3">
                        <h5 className="mb-0 fw-bold">
                            <FaUserEdit className="me-2" />
                            Información del Usuario
                        </h5>
                    </Card.Header>
                )}
                <Card.Body className={embedded ? 'p-0' : 'p-4'}>
                    <Form onSubmit={submitHandler}>
                        {/* DocIdGroup */}
                        <div className="mb-4">
                            <DocIdGroup
                                docId={docId}
                                setDocId={setDocId}
                                type={type}
                                setType={setType}
                                isForeigner={isForeigner}
                                setIsForeigner={setIsForeigner}
                                taxDocId={taxDocId}
                                setTaxDocId={setTaxDocId}
                                fillForm={fillForm}
                                setValidDocId={setValidDocId}
                            />
                        </div>

                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="name" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Nombre Completo</Form.Label>
                                    <Form.Control
                                        required={true}
                                        onInvalid={(e) =>
                                            e.target.setCustomValidity('Por favor, ingrese su Nombre')
                                        }
                                        type="text"
                                        placeholder="Ingrese su nombre completo"
                                        value={name}
                                        onChange={(e) => {
                                            setName(sanitizeInput(e.target.value, 'text'));
                                        }}
                                        className="py-2"
                                        size="lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="email" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Correo Electrónico</Form.Label>
                                    <Form.Control
                                        required={true}
                                        onInvalid={(e) =>
                                            e.target.setCustomValidity(
                                                'Por favor, ingrese su Correo Electónico'
                                            )
                                        }
                                        type="email"
                                        placeholder="ejemplo@correo.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="py-2"
                                        size="lg"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="password" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">
                                        Contraseña
                                        <small className="text-muted ms-2">(dejar en blanco para no cambiar)</small>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            required={false}
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Nueva contraseña"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                            size="lg"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </Button>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="confirmPassword" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Confirmar Contraseña</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            required={password}
                                            onInvalid={(e) =>
                                                e.target.setCustomValidity('Por favor, confirme su Contraseña')
                                            }
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            placeholder="Confirme la contraseña"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                            size="lg"
                                        />
                                        <Button
                                            variant="outline-secondary"
                                            className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                        </Button>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Role Management Section */}
                        <Card className="role-management-card mb-4 border-0">
                            <Card.Body className="p-3">
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                    <FaShieldAlt className="text-primary" />
                                    Roles y Permisos
                                </h6>

                                {/* SuperAdmin Full Control */}
                                {loggedInUser.isSuperAdmin && (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group controlId="isAdmin" className="mb-3">
                                                <div className="form-check form-switch">
                                                    <Form.Check
                                                        type="switch"
                                                        label="Acceso de Administrador"
                                                        checked={isAdmin}
                                                        onChange={(e) => setIsAdmin(e.target.checked)}
                                                        className="fs-6"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group controlId="isSuperAdmin" className="mb-3">
                                                <div className="form-check form-switch">
                                                    <Form.Check
                                                        type="switch"
                                                        label="Acceso de SuperAdmin"
                                                        checked={isSuperAdmin}
                                                        onChange={(e) => setIsSuperAdmin(e.target.checked)}
                                                        className="fs-6"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                )}

                                {/* Admin Limited Control */}
                                {loggedInUser.isAdmin && !loggedInUser.isSuperAdmin && (
                                    <>
                                        <Form.Group controlId="isAdmin" className="mb-3">
                                            <div className="form-check form-switch">
                                                <Form.Check
                                                    type="switch"
                                                    label="Acceso de Administrador"
                                                    checked={isAdmin}
                                                    onChange={(e) => setIsAdmin(e.target.checked)}
                                                    disabled={originalIsAdmin}
                                                    className="fs-6"
                                                />
                                            </div>
                                            {originalIsAdmin && (
                                                <small className="text-muted ms-4">
                                                    Solo un superadmin puede revocar acceso de administrador
                                                </small>
                                            )}
                                        </Form.Group>

                                        <Alert variant="info" className="py-2 px-3 mb-0">
                                            <div className="d-flex gap-2">
                                                <FaInfoCircle className="mt-1 flex-shrink-0" />
                                                <div>
                                                    <strong className="d-block mb-1">Permisos de Admin:</strong>
                                                    <ul className="mb-0 small ps-3">
                                                        <li>✓ Puedes editar información de usuario</li>
                                                        <li>✗ No puedes revocar acceso de administrador</li>
                                                        <li>✗ No puedes asignar roles de superadmin</li>
                                                        <li>✗ No puedes administrar permisos por empresa</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </Alert>
                                    </>
                                )}

                                {isAdmin && (
                                    <div className="mt-3 pt-3 border-top">
                                        <h6 className="fw-bold mb-3">Permisos por Empresa</h6>

                                        {isSuperAdmin ? (
                                            <Alert variant="secondary" className="mb-0">
                                                Usuario superadmin: no requiere permisos por empresa porque tiene acceso total.
                                            </Alert>
                                        ) : canEditCompanyPermissions ? (
                                            <>
                                                <Row className="g-2 align-items-end mb-3">
                                                    <Col md={6}>
                                                        <Form.Group controlId="companyRuc">
                                                            <Form.Label column={false} className="small mb-1">Empresa</Form.Label>
                                                            <Form.Select
                                                                value={selectedCompanyRuc}
                                                                onChange={(e) => setSelectedCompanyRuc(e.target.value)}
                                                                disabled={loadingCompanies}
                                                            >
                                                                <option value="">Seleccionar empresa...</option>
                                                                {availableCompanies?.map((company) => (
                                                                    <option key={company.ruc} value={company.ruc}>
                                                                        {company.tradeName} ({company.ruc})
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Group controlId="companyRole">
                                                            <Form.Label column={false} className="small mb-1">Rol en empresa</Form.Label>
                                                            <Form.Select
                                                                value={selectedCompanyRole}
                                                                onChange={(e) => setSelectedCompanyRole(e.target.value)}
                                                            >
                                                                <option value="ADMIN">ADMIN</option>
                                                                <option value="OPERATOR">OPERATOR</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={2}>
                                                        <Button
                                                            type="button"
                                                            variant="outline-primary"
                                                            className="w-100"
                                                            onClick={handleAddCompanyPermission}
                                                        >
                                                            Agregar
                                                        </Button>
                                                    </Col>
                                                </Row>

                                                {companies.length === 0 ? (
                                                    <Alert variant="warning" className="mb-0 py-2">
                                                        Debes asignar al menos una empresa para usuarios admin que no son superadmin.
                                                    </Alert>
                                                ) : (
                                                    <div className="d-flex flex-column gap-2">
                                                        {companies.map((company) => (
                                                            <div
                                                                key={company.ruc}
                                                                className="d-flex align-items-center justify-content-between border rounded p-2"
                                                            >
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Badge bg="light" text="dark">{company.ruc}</Badge>
                                                                    <span>{getCompanyName(company.ruc)}</span>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Form.Select
                                                                        size="sm"
                                                                        value={company.role}
                                                                        onChange={(e) =>
                                                                            handleCompanyRoleChange(company.ruc, e.target.value)
                                                                        }
                                                                    >
                                                                        <option value="ADMIN">ADMIN</option>
                                                                        <option value="OPERATOR">OPERATOR</option>
                                                                    </Form.Select>
                                                                    <Button
                                                                        type="button"
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleRemoveCompanyPermission(company.ruc)}
                                                                    >
                                                                        Quitar
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <Alert variant="info" className="mb-0">
                                                Solo un superadmin puede asignar o modificar permisos por empresa.
                                            </Alert>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <Row>
                            <Col md={12}>
                                <Form.Group controlId="priceAssigned" className="mb-4">
                                    <Form.Label column='lg' className="fw-bold">Precio Asignado</Form.Label>
                                    <Form.Select
                                        value={priceAssigned}
                                        onChange={(e) => setPriceAssigned(e.target.value)}
                                        className="py-2"
                                        size="lg"
                                    >
                                        <option value="pvp_1">PVP 1 (Público)</option>
                                        <option value="pvp_distribuidor">PVP Distribuidor</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex gap-2 mt-4">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={isLoading}
                                className="px-4 fw-bold"
                            >
                                {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                            </Button>
                            {embedded && (
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    size="lg"
                                    onClick={onClose}
                                    className="px-4"
                                >
                                    Cancelar
                                </Button>
                            )}
                        </div>

                        {isLoading && <Loader />}
                    </Form>
                </Card.Body>
            </Card>
        </>
    );

    return embedded ? formContent : <FormContainer>{formContent}</FormContainer>;
};

export default UserEditScreen;
